import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdArrowUpward, MdBusiness, MdChevronRight } from "react-icons/md";
import { formatLanguageOptionLabel, toLanguageOption } from "../../../shared/i18n/languageCatalog";
import { useI18n } from "../../../shared/i18n/i18n";
import { IoLocationOutline } from "react-icons/io5";
import { RiSignpostLine } from "react-icons/ri";
import {
  AdminOrganizationListRequest,
  AdminOrganizationListItem,
  AdminSignListRequest,
  AdminSignListItem,
  AdminSiteListRequest,
  AdminSiteListItem,
  AdminTranslationListRequest,
  AdminTranslationListItem,
} from "../../types/adminEntities";
import { FaLanguage } from "react-icons/fa";
import { listAdminOrganizations, listAdminSigns, listAdminSites, listAdminTranslations } from "../../../shared/requests";
import { useAdminLoading } from "./AdminLoadingContext";

export type AdminNavLevel = "organizations" | "signs" | "sites" | "translations";
export type AdminEntityType = "organization" | "sign" | "site" | "translation";
type AdminNavSortValue =
  | "CREATED_AT_ASC"
  | "CREATED_AT_DESC"
  | "LANGUAGE_ASC"
  | "LANGUAGE_DESC"
  | "NAME_ASC"
  | "NAME_DESC";
export type AdminNavContext = {
  organizationId: null | string;
  level: AdminNavLevel;
  signId: null | string;
  siteId: null | string;
};
export type AdminNavSelection = {
  organizationId?: string;
  organizationTab?: "details" | "domains" | "internals" | "subscription" | "users";
  create?: boolean;
  signTab?: "details" | "urls";
  signId?: string;
  siteTab?: "details";
  siteId?: string;
  translationTab?: "details";
  translationId?: string;
  type: AdminEntityType;
};

const PARENT_ENTITY_TYPE_BY_LEVEL: Partial<Record<AdminNavLevel, AdminEntityType>> = {
  signs: "site",
  sites: "organization",
  translations: "sign",
};

type AdminNavListProps = {
  contextOverride?: AdminNavContext;
  isMobileViewport?: boolean;
  onContextChange?: (context: AdminNavContext) => void;
  onSelectEntity?: (selection: AdminNavSelection | null) => void;
  reloadKey?: number;
  selectedEntity?: AdminNavSelection | null;
};

const LIST_LIMIT = 25;
const safeText = (value: null | string | undefined) => (value && value.trim().length > 0 ? value : "");
const DEFAULT_SORT_BY_LEVEL: Record<AdminNavLevel, AdminNavSortValue> = {
  organizations: "NAME_ASC",
  signs: "NAME_ASC",
  sites: "NAME_ASC",
  translations: "LANGUAGE_ASC",
};

const parseSortValue = (value: AdminNavSortValue) => {
  const sortDirection = value.endsWith("_DESC") ? "DESC" : "ASC";
  const sortField = value.replace(/_(ASC|DESC)$/, "");
  return {
    sortDirection: sortDirection as "ASC" | "DESC",
    sortField: sortField as "CREATED_AT" | "LANGUAGE" | "NAME",
  };
};

const createOrganizationsListRequest = (
  offset: number,
  includeArchived: boolean,
  searchTerm: string,
  sortValue: AdminNavSortValue,
): AdminOrganizationListRequest => ({
  includeArchived,
  limit: LIST_LIMIT,
  offset,
  searchTerm,
  ...parseSortValue(sortValue),
});

const createSitesListRequest = (
  organizationId: string,
  offset: number,
  includeArchived: boolean,
  searchTerm: string,
  sortValue: AdminNavSortValue,
): AdminSiteListRequest => ({
  organizationId,
  includeArchived,
  limit: LIST_LIMIT,
  offset,
  searchTerm,
  ...parseSortValue(sortValue),
});

const createSignsListRequest = (
  organizationId: string,
  siteId: string,
  offset: number,
  includeArchived: boolean,
  searchTerm: string,
  sortValue: AdminNavSortValue,
): AdminSignListRequest => ({
  organizationId,
  includeArchived,
  limit: LIST_LIMIT,
  offset,
  searchTerm,
  siteId,
  ...parseSortValue(sortValue),
});

const createTranslationsListRequest = (
  organizationId: string,
  siteId: string,
  signId: string,
  offset: number,
  includeArchived: boolean,
  sortValue: AdminNavSortValue,
): AdminTranslationListRequest => ({
  organizationId,
  includeArchived,
  limit: LIST_LIMIT,
  offset,
  signId,
  siteId,
  ...parseSortValue(sortValue),
});

type NavRecord = AdminOrganizationListItem | AdminSignListItem | AdminSiteListItem | AdminTranslationListItem;

export const AdminNavList = ({
  contextOverride,
  isMobileViewport = false,
  onContextChange,
  onSelectEntity,
  reloadKey = 0,
  selectedEntity = null,
}: AdminNavListProps) => {
  const { t } = useI18n();
  const { track } = useAdminLoading();
  const [level, setLevel] = useState<AdminNavLevel>("organizations");
  const [organization, setOrganization] = useState<AdminOrganizationListItem | null>(null);
  const [site, setSite] = useState<AdminSiteListItem | null>(null);
  const [sign, setSign] = useState<AdminSignListItem | null>(null);

  const [items, setItems] = useState<Array<AdminOrganizationListItem | AdminSignListItem | AdminSiteListItem | AdminTranslationListItem>>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [sortByLevel, setSortByLevel] = useState<Record<AdminNavLevel, AdminNavSortValue>>(DEFAULT_SORT_BY_LEVEL);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);

  const listRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);
  const normalizedSearchTerm = appliedSearchTerm.trim();
  const requestSearchTerm = level === "translations" ? "" : normalizedSearchTerm;
  const parentEntityType = PARENT_ENTITY_TYPE_BY_LEVEL[level];

  useEffect(() => {
    if (!contextOverride) {
      return;
    }

    const nextLevel = contextOverride.level;
    if (nextLevel !== level) {
      setLevel(nextLevel);
    }

    const nextOrganizationId = contextOverride.organizationId;
    setOrganization((prev) => {
      if (!nextOrganizationId) {
        return null;
      }
      if (prev?.id === nextOrganizationId) {
        return prev;
      }
      return { archivedAt: null, createdAt: "", description: null, id: nextOrganizationId, name: "" };
    });

    const nextSiteId = contextOverride.siteId;
    setSite((prev) => {
      if (!nextSiteId) {
        return null;
      }
      if (prev?.id === nextSiteId) {
        return prev;
      }
      return { organizationId: nextOrganizationId ?? "", archivedAt: null, createdAt: "", description: null, id: nextSiteId, name: "" };
    });

    const nextSignId = contextOverride.signId;
    setSign((prev) => {
      if (!nextSignId) {
        return null;
      }
      if (prev?.id === nextSignId) {
        return prev;
      }
      return { archivedAt: null, createdAt: "", description: null, id: nextSignId, name: "", siteId: nextSiteId ?? "", uuid: "" };
    });
  }, [contextOverride, level]);

  const listTitle = useMemo(() => {
    if (level === "organizations") {
      return t("admin.navlist.title.organizations");
    }
    if (level === "sites") {
      return organization?.name ?? t("admin.navlist.title.sites");
    }
    if (level === "signs") {
      return site?.name ?? t("admin.navlist.title.signs");
    }
    return sign?.name ?? t("admin.navlist.title.translations");
  }, [organization, level, sign, site, t]);

  const parentFolderLabel = useMemo(() => {
    if (level === "sites") {
      return t("admin.navlist.parentFolder.organizations");
    }
    if (level === "signs") {
      return t("admin.navlist.parentFolder.sites");
    }
    if (level === "translations") {
      return t("admin.navlist.parentFolder.signs");
    }
    return t("admin.navlist.parentFolder");
  }, [level, t]);

  useEffect(() => {
    setAppliedSearchTerm("");
    setSearchDraft("");
  }, [level, organization?.id, site?.id, sign?.id]);

  const filteredItems = useMemo(() => {
    if (level !== "translations" || normalizedSearchTerm.length === 0) {
      return items;
    }
    const normalizedNeedle = normalizedSearchTerm.toLowerCase();
    return items.filter((record) => {
      const languageOption = toLanguageOption((record as AdminTranslationListItem).language);
      return [
        languageOption.englishLabel,
        languageOption.nativeLabel,
        languageOption.tag,
      ].some((value) => value.toLowerCase().includes(normalizedNeedle));
    });
  }, [items, level, normalizedSearchTerm]);

  const canLoadMore = !(level === "translations" && normalizedSearchTerm.length > 0) && items.length < totalCount;
  const searchPlaceholder = level === "translations"
    ? t("admin.navlist.searchLanguagesPlaceholder")
    : t("admin.navlist.searchPlaceholder");
  const selectedSort = sortByLevel[level];
  const sortOptions = level === "translations"
    ? [
        { label: t("admin.navlist.sort.languageAsc"), value: "LANGUAGE_ASC" as const },
        { label: t("admin.navlist.sort.languageDesc"), value: "LANGUAGE_DESC" as const },
        { label: t("admin.navlist.sort.createdAtAsc"), value: "CREATED_AT_ASC" as const },
        { label: t("admin.navlist.sort.createdAtDesc"), value: "CREATED_AT_DESC" as const },
      ]
    : [
        { label: t("admin.navlist.sort.nameAsc"), value: "NAME_ASC" as const },
        { label: t("admin.navlist.sort.nameDesc"), value: "NAME_DESC" as const },
        { label: t("admin.navlist.sort.createdAtAsc"), value: "CREATED_AT_ASC" as const },
        { label: t("admin.navlist.sort.createdAtDesc"), value: "CREATED_AT_DESC" as const },
      ];

  const loadPage = useCallback(
    async (nextOffset: number, replace: boolean) => {
      const currentRequestId = ++requestIdRef.current;
      setIsLoading(true);
      setErrorMessage(null);

      let requestPromise:
        | ReturnType<typeof listAdminOrganizations>
        | ReturnType<typeof listAdminSigns>
        | ReturnType<typeof listAdminSites>
        | ReturnType<typeof listAdminTranslations>;

      if (level === "organizations") {
        requestPromise = listAdminOrganizations(createOrganizationsListRequest(nextOffset, includeArchived, requestSearchTerm, selectedSort));
      } else if (level === "sites") {
        if (!organization?.id) {
          setIsLoading(false);
          setErrorMessage(t("admin.navlist.error.selectOrganization"));
          return;
        }
        requestPromise = listAdminSites(createSitesListRequest(organization.id, nextOffset, includeArchived, requestSearchTerm, selectedSort));
      } else if (level === "signs") {
        if (!organization?.id || !site?.id) {
          setIsLoading(false);
          setErrorMessage(t("admin.navlist.error.selectSite"));
          return;
        }
        requestPromise = listAdminSigns(createSignsListRequest(organization.id, site.id, nextOffset, includeArchived, requestSearchTerm, selectedSort));
      } else if (level === "translations") {
        if (!organization?.id || !site?.id || !sign?.id) {
          setIsLoading(false);
          setErrorMessage(t("admin.navlist.error.selectSign"));
          return;
        }
        requestPromise = listAdminTranslations(createTranslationsListRequest(organization.id, site.id, sign.id, nextOffset, includeArchived, selectedSort));
      } else {
        setIsLoading(false);
        return;
      }

      try {
        const { payload } = await track(async () => requestPromise);
        if (requestIdRef.current !== currentRequestId) {
          return;
        }
        const pageItems = Array.isArray(payload.items) ? payload.items : [];
        setItems((prev) => (replace ? pageItems : [...prev, ...pageItems]));
        setOffset(nextOffset + pageItems.length);
        setTotalCount(typeof payload.totalCount === "number" ? payload.totalCount : pageItems.length);
      } catch (error) {
        if (requestIdRef.current !== currentRequestId) {
          return;
        }
        const fallbackMessage = t("admin.navlist.error.loadFailed");
        const message = error instanceof Error ? error.message : fallbackMessage;
        setErrorMessage(message || fallbackMessage);
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false);
        }
      }
    },
    [organization?.id, includeArchived, level, requestSearchTerm, selectedSort, sign?.id, site?.id, t, track]
  );

  const resetAndLoad = useCallback(() => {
    setItems([]);
    setOffset(0);
    setTotalCount(0);
    void loadPage(0, true);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [loadPage]);

  useEffect(() => {
    resetAndLoad();
  }, [level, organization?.id, site?.id, sign?.id, includeArchived, reloadKey, requestSearchTerm, resetAndLoad, selectedSort]);

  useEffect(() => {
    const root = listRef.current;
    const target = loadMoreRef.current;
    if (!root || !target || isLoading || !canLoadMore) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          void loadPage(offset, false);
        }
      },
      { root, rootMargin: "0px 0px 120px 0px", threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [canLoadMore, isLoading, loadPage, offset]);

  const handleNavigateUp = () => {
    if (level === "sites") {
      if (organization?.id) {
        onSelectEntity?.({ organizationId: organization.id, type: "organization" });
        return;
      }
      setOrganization(null);
      setSite(null);
      setSign(null);
      setLevel("organizations");
      onContextChange?.({ organizationId: null, level: "organizations", signId: null, siteId: null });
    } else if (level === "signs") {
      if (organization?.id && site?.id) {
        onSelectEntity?.({ organizationId: organization.id, siteId: site.id, type: "site" });
        return;
      }
      setSite(null);
      setSign(null);
      setLevel("sites");
      onContextChange?.({ organizationId: organization?.id ?? null, level: "sites", signId: null, siteId: null });
    } else if (level === "translations") {
      if (organization?.id && site?.id && sign?.id) {
        onSelectEntity?.({ organizationId: organization.id, signId: sign.id, siteId: site.id, type: "sign" });
        return;
      }
      setSign(null);
      setLevel("signs");
      onContextChange?.({ organizationId: organization?.id ?? null, level: "signs", signId: null, siteId: site?.id ?? null });
    }
  };

  const handleOrganizationOpen = (record: OrganizationRecord) => {
    setOrganization(record);
    setSite(null);
    setSign(null);
    setLevel("sites");
    onContextChange?.({ organizationId: record.id, level: "sites", signId: null, siteId: null });
  };

  const handleSiteOpen = (record: SiteRecord) => {
    setSite(record);
    setSign(null);
    setLevel("signs");
    onContextChange?.({ organizationId: record.organizationId, level: "signs", signId: null, siteId: record.id });
  };

  const handleSignOpen = (record: SignRecord) => {
    setSign(record);
    setLevel("translations");
    onContextChange?.({
      organizationId: organization?.id ?? null,
      level: "translations",
      signId: record.id,
      siteId: record.siteId,
    });
  };

  const handleOrganizationSelect = (record: OrganizationRecord) => {
    onSelectEntity?.({ organizationId: record.id, type: "organization" });
  };

  const handleSiteSelect = (record: SiteRecord) => {
    if (!organization?.id) {
      return;
    }
    onSelectEntity?.({ organizationId: organization.id, siteId: record.id, type: "site" });
  };

  const handleSignSelect = (record: SignRecord) => {
    if (!organization?.id || !site?.id) {
      return;
    }
    onSelectEntity?.({ organizationId: organization.id, signId: record.id, siteId: site.id, type: "sign" });
  };

  const handleTranslationSelect = (record: TranslationRecord) => {
    if (!organization?.id || !site?.id || !sign?.id) {
      return;
    }
    onSelectEntity?.({
      organizationId: organization.id,
      signId: sign.id,
      siteId: site.id,
      translationId: record.id,
      type: "translation",
    });
  };

  const handlePrimaryAction = (onSelect: () => void) => {
    onSelect();
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAppliedSearchTerm(searchDraft.trim());
  };

  const isEntitySelected = (entitySelection: AdminNavSelection) => {
    if (!selectedEntity || selectedEntity.create) {
      return false;
    }

    return selectedEntity.type === entitySelection.type
      && selectedEntity.organizationId === entitySelection.organizationId
      && selectedEntity.siteId === entitySelection.siteId
      && selectedEntity.signId === entitySelection.signId
      && selectedEntity.translationId === entitySelection.translationId;
  };

  const renderNavRow = ({
    entityType,
    icon,
    isArchived = false,
    isSelected = false,
    key,
    label,
    meta,
    onOpen,
    onSelect,
    title,
  }: {
    entityType: AdminEntityType;
    icon: JSX.Element;
    isArchived?: boolean;
    isSelected?: boolean;
    key: string;
    label: string;
    meta?: string;
    onOpen?: () => void;
    onSelect: () => void;
    title?: string;
  }) => (
    <div className={`admin-nav-list-row type-${entityType} ${onOpen ? "has-secondary-action" : ""} ${isArchived ? "is-archived" : ""}`} key={key}>
      <button
        aria-current={isSelected ? "true" : undefined}
        className={`admin-nav-list-item type-${entityType} ${isSelected ? "is-active" : ""} ${isArchived ? "is-archived" : ""}`}
        onClick={() => handlePrimaryAction(onSelect)}
        type="button"
      >
        <span aria-hidden="true" className="admin-nav-list-item-icon">
          {icon}
        </span>
        <span className="admin-nav-list-item-text">
          <span
            className="admin-nav-list-item-label"
            title={title ?? label}
          >
            {label}
          </span>
          {meta ? (
            <span
              className="admin-nav-list-item-meta"
              title={meta}
            >
              {meta}
            </span>
          ) : null}
        </span>
      </button>
      {onOpen ? (
        <button
          aria-label={t("admin.navlist.openChildren")}
          className={`admin-nav-list-item-action type-${entityType} ${isArchived ? "is-archived" : ""}`}
          onClick={onOpen}
          title={t("admin.navlist.openChildren")}
          type="button"
        >
          <MdChevronRight />
        </button>
      ) : null}
    </div>
  );

  const renderRow = (record: NavRecord) => {
    if (level === "organizations") {
      const organizationRecord = record as AdminOrganizationListItem;
      return renderNavRow({
        entityType: "organization",
        icon: <MdBusiness />,
        isArchived: Boolean(organizationRecord.archivedAt),
        isSelected: isEntitySelected({ organizationId: organizationRecord.id, type: "organization" }),
        key: `organization-${organizationRecord.id}`,
        label: organizationRecord.name,
        meta: safeText(organizationRecord.description) || undefined,
        onOpen: () => handleOrganizationOpen(organizationRecord),
        onSelect: () => handleOrganizationSelect(organizationRecord),
        title: safeText(organizationRecord.name) || undefined,
      });
    }

    if (level === "sites") {
      const siteRecord = record as AdminSiteListItem;
      return renderNavRow({
        entityType: "site",
        icon: <IoLocationOutline />,
        isArchived: Boolean(siteRecord.archivedAt),
        isSelected: Boolean(organization?.id) && isEntitySelected({ organizationId: organization.id, siteId: siteRecord.id, type: "site" }),
        key: `site-${siteRecord.id}`,
        label: siteRecord.name,
        meta: safeText(siteRecord.description) || undefined,
        onOpen: () => handleSiteOpen(siteRecord),
        onSelect: () => handleSiteSelect(siteRecord),
        title: safeText(siteRecord.name) || undefined,
      });
    }

    if (level === "signs") {
      const signRecord = record as AdminSignListItem;
      return renderNavRow({
        entityType: "sign",
        icon: <RiSignpostLine />,
        isArchived: Boolean(signRecord.archivedAt),
        isSelected: Boolean(organization?.id && site?.id)
          && isEntitySelected({ organizationId: organization!.id, signId: signRecord.id, siteId: site!.id, type: "sign" }),
        key: `sign-${signRecord.id}`,
        label: safeText(signRecord.name) || signRecord.uuid,
        meta: safeText(signRecord.description) || undefined,
        onOpen: () => handleSignOpen(signRecord),
        onSelect: () => handleSignSelect(signRecord),
        title: safeText(signRecord.name) || signRecord.uuid || undefined,
      });
    }

    const translationRecord = record as AdminTranslationListItem;
    const languageOption = toLanguageOption(translationRecord.language);
    const languageLabel = languageOption.englishLabel || languageOption.tag;
    const nativeLanguageLabel = languageOption.nativeLabel || languageOption.tag;
    return renderNavRow({
      entityType: "translation",
      icon: <FaLanguage />,
      isArchived: Boolean(translationRecord.archivedAt),
      isSelected: Boolean(organization?.id && site?.id && sign?.id)
        && isEntitySelected({
          organizationId: organization!.id,
          signId: sign!.id,
          siteId: site!.id,
          translationId: translationRecord.id,
          type: "translation",
        }),
      key: `translation-${translationRecord.id}`,
      label: languageLabel,
      meta: nativeLanguageLabel,
      onSelect: () => handleTranslationSelect(translationRecord),
      title: safeText(languageLabel) || undefined,
    });
  };

  return (
    <div className="admin-nav-list-wrap">
      <div className="admin-nav-list-header">
        <p className="admin-nav-list-title">{t("admin.navlist.navigator")}</p>
        <p className="admin-nav-list-context">{listTitle}</p>
        <form className="admin-nav-list-search-form" onSubmit={handleSearchSubmit}>
          <input
            className="admin-nav-list-search"
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder={searchPlaceholder}
            type="search"
            value={searchDraft}
          />
        </form>
        <label className="admin-nav-list-sort">
          <span>{t("admin.navlist.sort.label")}</span>
          <select
            className="admin-nav-list-sort-select"
            onChange={(event) => {
              const nextValue = event.target.value as AdminNavSortValue;
              setSortByLevel((prev) => ({ ...prev, [level]: nextValue }));
            }}
            value={selectedSort}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="admin-nav-list-archived-toggle">
          <input
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
            type="checkbox"
          />
          <span>{t("admin.navlist.includeArchived")}</span>
        </label>
      </div>
      <div className="admin-nav-list" ref={listRef}>
        {level !== "organizations" ? (
          <button
            className={`admin-nav-list-item is-parent ${parentEntityType ? `type-${parentEntityType}` : ""}`}
            onClick={handleNavigateUp}
            type="button"
          >
            <span aria-hidden="true" className="admin-nav-list-item-icon">
              <MdArrowUpward />
            </span>
            <span className="admin-nav-list-item-text">
              <span
                className="admin-nav-list-item-label"
                title={t("admin.navlist.parentFolderLabel")}
              >
                {t("admin.navlist.parentFolderLabel")}
              </span>
              <span
                className="admin-nav-list-item-meta"
                title={parentFolderLabel}
              >
                {parentFolderLabel}
              </span>
            </span>
          </button>
        ) : null}
        {filteredItems.map((record) => renderRow(record))}
        {isLoading ? (
          <div className="admin-nav-list-state">{t("admin.navlist.loading")}</div>
        ) : null}
        {!isLoading && filteredItems.length === 0 ? (
          <div className="admin-nav-list-state">{normalizedSearchTerm.length > 0 ? t("admin.navlist.emptySearch") : t("admin.navlist.empty")}</div>
        ) : null}
        {errorMessage ? (
          <div className="admin-nav-list-state is-error">{errorMessage}</div>
        ) : null}
        {canLoadMore ? <div aria-hidden="true" className="admin-nav-list-load-more-sentinel" ref={loadMoreRef} /> : null}
        {!isLoading && canLoadMore ? (
          <div className="admin-nav-list-state">{t("admin.navlist.scrollForMore")}</div>
        ) : null}
      </div>
    </div>
  );
};
